import type { SurveyResponseDto } from '@hotjar/feedback/domain/Surveys/Responses/SurveyResponseDto';
import type { SurveyQuestion } from './SurveyQuestion';

export interface QuestionAnswer {
  answer: string | number;
  comment: string | undefined;
}

export interface QuestionWithAnswers {
  uuid: string;
  text: string;
  answers: QuestionAnswer[];
}

/**
 * Group all questions/answers/comments in a survey response using the question UUID as a key.
 *
 * @param response A complete SurveyResponse.
 * @param questionsById A list of Survey questions grouped by question UUID.
 */
export function groupAnswersByQuestionId(
  response: SurveyResponseDto,
  questionsById: Record<string, SurveyQuestion>,
): Record<string, QuestionWithAnswers> {
  const questionsWithAnswers = {} as Record<string, QuestionWithAnswers>;

  response.content.answers.forEach(function assignAnswersToQuestions(questionAnswer) {
    const matchingQuestion = questionsById[questionAnswer.questionUuid];

    if (!matchingQuestion) {
      return;
    }

    const questionUuid = matchingQuestion.uuid;

    if (typeof questionsWithAnswers[questionUuid] === 'undefined') {
      questionsWithAnswers[questionUuid] = {
        uuid: questionUuid,
        text: matchingQuestion.text,
        answers: [],
      };
    }

    // There can be multiple answers from one respondent
    questionsWithAnswers[questionUuid].answers.push({
      answer: questionAnswer.answer,
      comment: questionAnswer.comment,
    });
  });

  return questionsWithAnswers;
}
